/**
 * Boundary adapters for the Communications Center.
 *
 * The OpenAPI document declares every paged payload as `PagedModel`, whose
 * `content` is an array with no item schema, so the client-search payload is
 * narrowed here rather than asserted at the call site. The field names below
 * were verified against staging, which answered
 * `{id, cellphone, email, notificationPushToken}` until 2026-09-22 and now
 * answers `{id, name, cellphone, email, isActive}` — channel-shaped, and with
 * no push token in it. Both are read: the names that disappeared cost nothing
 * to keep asking for, and a recipient still falls back to its contact when no
 * name comes back.
 */

import { CommunicationRecipient } from "@/types/communicationsCenter";
import { CommunicationTypeValues } from "@/types/refCommunicationTemplate";

const readString = (source: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim() !== "") return value;
  }
  return undefined;
};

const readNumber = (source: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return undefined;
};

/**
 * Narrow one raw entry into a recipient. Entries without an id are dropped by
 * `adaptRecipients` rather than rendered as a row we cannot act on.
 */
export const adaptRecipient = (raw: unknown): CommunicationRecipient | null => {
  if (raw == null || typeof raw !== "object") return null;

  const source = raw as Record<string, unknown>;
  const id = readNumber(source, "id", "clientId");
  if (id == null) return null;

  return {
    id,
    name: readString(source, "name"),
    email: readString(source, "email"),
    cellphone: readString(source, "cellphone", "phone", "phoneNumber"),
    notificationPushToken: readString(
      source,
      "notificationPushToken",
      "pushToken",
    ),
  };
};

export const adaptRecipients = (raw: unknown): CommunicationRecipient[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .map(adaptRecipient)
    .filter(
      (recipient): recipient is CommunicationRecipient => recipient != null,
    );
};

/**
 * The contact a channel actually sends to. Recipients without one are excluded
 * from a send instead of being posted as an empty string — notifications go to
 * the push token, not to a phone number.
 */
export const getRecipientContact = (
  recipient: CommunicationRecipient,
  channel: CommunicationTypeValues,
): string | undefined => {
  if (channel === "EMAIL") return recipient.email;
  if (channel === "NOTIFICATION") return recipient.notificationPushToken;
  return recipient.cellphone;
};

/** How a recipient is labelled in the UI when only one line is available. */
export const describeRecipient = (
  recipient: CommunicationRecipient,
  channel: CommunicationTypeValues,
): string =>
  getRecipientContact(recipient, channel) ??
  recipient.email ??
  recipient.cellphone ??
  recipient.name ??
  `Cliente ${recipient.id}`;
